/**
 * Workspace switcher pill (US-004) — sibling of RuntimeSwitcher.
 *
 * Renders the active workspace's name as a small pill in the topbar.
 * Clicking opens a dropdown of the workspaces the current user belongs
 * to. Selecting one updates `ActiveWorkspaceContext` (which clears the
 * active runtime, persists the slug to localStorage + a cookie, and
 * pushes the slug into api.* so subsequent requests are scoped).
 *
 * Single-workspace installs render the pill in a passive read-only
 * style — no dropdown, no chevron — to keep the topbar quiet for
 * users that never need multi-tenancy.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UiIcons } from '@pipebase/ui';
import { useActiveWorkspace } from '../runtime/ActiveWorkspaceContext';

export function WorkspaceSwitcher() {
  const { workspaces, active, setActive } = useActiveWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (!active) return null; // List not loaded yet — keep topbar tidy.

  // Single-workspace installs: render a passive label.
  const passive = workspaces.length <= 1;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => {
          if (!passive) setOpen((o) => !o);
        }}
        title={passive ? `Workspace: ${active.name}` : `Switch workspace (current: ${active.name})`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 30,
          padding: '0 12px 0 10px',
          background: 'rgba(168, 85, 247, 0.10)',
          border: '1px solid rgba(168, 85, 247, 0.45)',
          borderRadius: 100,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-1)',
          cursor: passive ? 'default' : 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#a855f7',
            boxShadow: '0 0 6px #a855f7',
          }}
        />
        <span>{active.name}</span>
        {/* Data residency chip (US-005) — surfaces the workspace's data residency. */}
        {(() => {
          const r = (active.settings as { dataResidency?: string } | undefined)?.dataResidency;
          if (!r || r === 'unspecified') return null;
          const colors: Record<string, { bg: string; fg: string }> = {
            eu: { bg: 'rgba(59, 130, 246, 0.18)', fg: '#3b82f6' },
            us: { bg: 'rgba(220, 38, 38, 0.18)', fg: '#dc2626' },
            apac: { bg: 'rgba(16, 185, 129, 0.18)', fg: '#10b981' },
          };
          const c = colors[r] ?? { bg: 'rgba(120,113,108,0.18)', fg: '#78716c' };
          return (
            <span
              title={`Data residency: ${r.toUpperCase()}`}
              style={{
                marginLeft: 2,
                padding: '1px 6px',
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                background: c.bg,
                color: c.fg,
                borderRadius: 6,
              }}
            >
              {r}
            </span>
          );
        })()}
        {!passive && (
          <span style={{ color: 'var(--text-4)', marginLeft: 2 }}>{UiIcons.chevronDown}</span>
        )}
      </button>

      {open && !passive && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 240,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 6,
            zIndex: 60,
          }}
        >
          <div
            style={{
              padding: '6px 10px',
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-4)',
            }}
          >
            Switch workspace
          </div>
          {workspaces.map((w) => {
            const isActive = w.id === active.id;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => {
                  setActive(w.slug);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  background: isActive ? 'var(--bg-tint)' : 'transparent',
                  border: 0,
                  color: 'var(--text-1)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {w.name}
                </span>
                <span
                  style={{
                    color: 'var(--text-4)',
                    fontSize: 11,
                    fontFamily: 'var(--mono, monospace)',
                  }}
                >
                  {w.slug}
                </span>
              </button>
            );
          })}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              marginTop: 4,
              paddingTop: 4,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/settings/workspace');
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '7px 10px',
                background: 'transparent',
                border: 0,
                color: 'var(--text-2)',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 12.5,
              }}
            >
              Workspace settings…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
